import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { LoaderIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'store';
import { saveFile, savePromptInfo, setFileName } from 'store/slices/imageGen';
import z from 'zod';
import { IMAGE_GEN_AVAILABLE_STYLE } from '../constants/data';
import { ImageGenService } from '../services/image-gen.api';
import ExternalTools from './ExternalTools';
import RegenerateModal from './modals/RegenerateModal';
import ApplyChangesDialog from './modals/ApplyChangesDialog';

const schema = z.object({
  input: z.string().min(5, {
    message: 'This field cannot be empty',
  }),
  prompt_type: z.string(),
  token: z.string().optional(),
});

type TSchema = z.infer<typeof schema>;

export default function EditImageForm() {
  const [selectedStyleType, setSelectedStyleType] = useState('Digital Style');
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isSaved, input, prompt_type } = useSelector(
    (state: RootState) => state.imageGen
  );

  const { mutate, isPending } = useMutation({
    mutationKey: ['generate-image-from-text'],
    mutationFn: ImageGenService.generateImageFromText,
    onSuccess: (data) => {
      navigate(`/text-to-image/result`);
      dispatch(
        savePromptInfo({
          tempImagePath: data.data.image_url,
          prompt_type: form.getValues('prompt_type'),
          input: form.getValues('input'),
        })
      );
      dispatch(setFileName({ fileName: 'Untitled 1' }));
      dispatch(saveFile({ fileSaved: false }));
      setIsAlertDialogOpen(false);
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const form = useForm<TSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      input,
      prompt_type,
    },
  });

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) return;
    const token = await executeRecaptcha('KaiRecaptcha0911');
    form.setValue('token', token);
  }, [executeRecaptcha]);

  const handleContinueWithoutSaving = async () => {
    setIsAlertDialogOpen(false);
    await handleReCaptchaVerify();
    mutate({
      input: form.getValues('input'),
      prompt_type: form.getValues('prompt_type'),
      token: form.getValues('token'),
    });
  };

  const handleApplyChanges = async () => {
    if (isSaved) {
      await handleReCaptchaVerify();
      mutate({
        input: form.getValues('input'),
        prompt_type: form.getValues('prompt_type'),
        token: form.getValues('token'),
      });
    } else {
      setIsAlertDialogOpen(true);
    }
  };

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const onSubmit = async (values: TSchema) => {
    await handleReCaptchaVerify();
    mutate(values);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    form.setValue('input', e.target.value);
  };

  return (
    <div className="bg-background-main text-text-primary flex flex-col rounded-md lg:p-3 lg:px-4 col-span-3 overflow-y-auto overflow-x-hidden">
      <p className="text-primary uppercase text-sm">Edit Image</p>

      <form className="mt-3 flex flex-1 flex-col gap-4 lg:px-0">
        <div className="space-y-2 border-b">
          <label className="text-sm">Prompt</label>

          <div className="hidden lg:block">
            <input
              {...form.register('input')}
              onChange={handleInputChange}
              placeholder="Enter prompt"
              className="w-full h-10 px-3 py-2 border bg-background border-gray-300 rounded-md"
            />
          </div>

          <div className="lg:hidden">
            <textarea
              {...form.register('input')}
              onChange={handleInputChange}
              placeholder="Enter prompt"
              className="w-full min-h-[120px] p-3 rounded-md bg-background border border-gray-300 resize-none"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm">Change style</label>

          <div className="flex items-start text-center font-medium text-sm text-primary rounded-md overflow-hidden bg-secondary">
            <button
              type="button"
              className={cn(
                'p-2 flex-1',
                selectedStyleType === 'Digital Style' && 'bg-primary text-white'
              )}
              onClick={() => setSelectedStyleType('Digital Style')}
            >
              Digital
            </button>
            <button
              type="button"
              className={cn(
                'p-2 flex-1',
                selectedStyleType === 'Traditional Style' &&
                  'bg-primary text-white'
              )}
              onClick={() => setSelectedStyleType('Traditional Style')}
            >
              Traditional
            </button>
          </div>

          <Controller
            control={form.control}
            name="prompt_type"
            render={({ field }) => (
              <>
                <div className="hidden lg:grid grid-cols-2 gap-4">
                  {selectedStyleType === 'Digital Style' &&
                    IMAGE_GEN_AVAILABLE_STYLE.digital_style.subStyles.map(
                      (style) => (
                        <button
                          key={style.id}
                          type="button"
                          className={cn(
                            'rounded-md overflow-hidden border bg-background',
                            field.value == style.id && 'border-primary'
                          )}
                          onClick={() => field.onChange(style.id)}
                        >
                          <img 
                            src={style.imagePath} 
                            alt={style.text}
                            className="w-full aspect-square object-cover"
                          />
                          <p className="p-2 text-xs">{style.text}</p>
                        </button>
                      )
                    )}
                  {selectedStyleType === 'Traditional Style' &&
                    IMAGE_GEN_AVAILABLE_STYLE.traditional_style.subStyles.map(
                      (style) => (
                        <button
                          key={style.id}
                          type="button"
                          className={cn(
                            'rounded-md overflow-hidden border bg-background',
                            field.value == style.id && 'border-primary'
                          )}
                          onClick={() => field.onChange(style.id)}
                        >
                          <img 
                            src={style.imagePath} 
                            alt={style.text}
                            className="w-full aspect-square object-cover"
                          />
                          <p className="p-2 text-xs">{style.text}</p>
                        </button>
                      )
                    )}
                </div>

                <div className="lg:hidden overflow-x-auto pb-4 -mx-4">
                  <div className="flex gap-4 px-4 min-w-min">
                    {selectedStyleType === 'Digital Style' &&
                      IMAGE_GEN_AVAILABLE_STYLE.digital_style.subStyles.map(
                        (style) => (
                          <button
                            key={style.id}
                            type="button"
                            className={cn(
                              'flex-none w-32 rounded-md overflow-hidden border bg-background',
                              field.value == style.id && 'border-primary'
                            )}
                            onClick={() => field.onChange(style.id)}
                          >
                            <img 
                              src={style.imagePath} 
                              alt={style.text}
                              className="w-full aspect-square object-cover"
                            />
                            <p className="p-2 text-xs">{style.text}</p>
                          </button>
                        )
                      )}
                    {selectedStyleType === 'Traditional Style' &&
                      IMAGE_GEN_AVAILABLE_STYLE.traditional_style.subStyles.map(
                        (style) => (
                          <button
                            key={style.id}
                            type="button"
                            className={cn(
                              'flex-none w-32 rounded-md overflow-hidden border bg-background',
                              field.value == style.id && 'border-primary'
                            )}
                            onClick={() => field.onChange(style.id)}
                          >
                            <img 
                              src={style.imagePath} 
                              alt={style.text}
                              className="w-full aspect-square object-cover"
                            />
                            <p className="p-2 text-xs">{style.text}</p>
                          </button>
                        )
                      )}
                  </div>
                </div>
              </>
            )}
          />
        </div>

        {/* Desktop Apply Button */}
        <div className="w-full mt-auto hidden lg:block">
          {isSaved ? (
            <Button
              disabled={!form.formState.isDirty || isPending}
              className="w-full bg-primary text-lg text-white"
              variant="customSecondary"
              onClick={handleApplyChanges}
            >
              {isPending && (
                <div className="animate-spin">
                  <LoaderIcon size={20} />
                </div>
              )}
              {isPending ? 'Applying' : 'Reapply Changes'}
            </Button>
          ) : (
            <ApplyChangesDialog
              isOpen={isAlertDialogOpen}
              setIsOpen={setIsAlertDialogOpen}
              isPending={isPending}
              isSaved={isSaved}
              handleContinueWithoutSaving={handleContinueWithoutSaving}
              formState={form.formState}
            />
          )}
        </div>

        {/* Desktop regenerate button   */}
        <div className="hidden lg:block">
          <p className="uppercase text-xs">
            Don&apos;t like this image?
          </p>
          <button
            type="button"
            onClick={() => setIsRegenerateModalOpen(true)}
            className="text-primaryAccent-9 text-left flex items-end mt-2 justify-center gap-2 w-full underline font-medium text-sm"
          >
            Regenerate image
          </button>
          <RegenerateModal 
            isOpen={isRegenerateModalOpen}
            onOpenChange={setIsRegenerateModalOpen}
          />
        </div>

        {/* Mobile Apply Button */}
        <div className="lg:hidden w-full text-lg">
          {isSaved ? (
            <Button
              disabled={!form.formState.isDirty || isPending}
              className="w-full text-lg"
              variant={'customPrimary'}
              onClick={handleApplyChanges}
            >
              {isPending && (
                <div className="animate-spin">
                  <LoaderIcon size={20} />
                </div>
              )}
              {isPending ? 'Applying' : 'Reapply changes'}
            </Button>
          ) : (
            <ApplyChangesDialog
              isOpen={isAlertDialogOpen}
              setIsOpen={setIsAlertDialogOpen}
              isPending={isPending}
              isSaved={isSaved}
              handleContinueWithoutSaving={handleContinueWithoutSaving}
              formState={form.formState}
            />
          )}
        </div>
      </form>

      <div className="hidden lg:block">
        <ExternalTools />
      </div>
    </div>
  );
}
